import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  deleteUser } from
"firebase/auth";
import { doc, getDoc, setDoc, runTransaction, onSnapshot } from "../services/fs";
import { endCurrentSession } from "../services/sessions";
import { clearReauth } from "../services/reauth";
import { auth, db } from "../config/firebase";
import { RoleService, UserService, LoginLogService, WorkspaceService, LicenseService } from "../services/firestore";
import { groupCapacity, setGroupCapacity } from "../services/groupQuota";
import { defaultWorkspaceId, licenseCodeOf, setReadOwner, setTenancy, setUserScope, setWorkspaceScope, tenantOf } from "../services/tenancy";
import { LegalEntityService, UserScopeService, activeLegalEntity, normalizeScope } from "../services/organization";
import {
  ROLES,
  normalizeRole,
  getPermissionsForRole,
  getDemoBrowsePermissions,
  hasPermission as checkPermission,
  isAdminPanelPermission,
  canWriteWithPermissions,
  isFullAdminEmail,
  getAssignableRoles,
  canManageUser
} from "../services/roles";
import { isDemoEmail, setDemoMode } from "../services/demoMode";
import { isForeignLicenseBlockedInTurkey } from "../services/licenseTurkeyGuard";
import { setSignOutReason } from "../services/sessionTimeout";

export const ACCOUNT_DISABLED = "auth/user-disabled";

function disabledError() {
  const err = new Error("ACCOUNT_DISABLED");
  err.code = ACCOUNT_DISABLED;
  return err;
}

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [permissions, setPermissions] = useState(() => getPermissionsForRole(ROLES.VIEWER));
  const [role, setRole] = useState(ROLES.USER);
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [organization, setOrganization] = useState({ tenantId: null, legalEntities: [], scope: null, legalEntityId: null });
  const [orgVersion, setOrgVersion] = useState(0);

  const applyProfile = useCallback((profile, emailHint) => {
    if (!profile) {
      setUserProfile(null);
      setRole(ROLES.USER);
      setPermissions(getPermissionsForRole(ROLES.USER));
      return;
    }
    const email = profile.email || emailHint;
    const nextRole = normalizeRole(profile.role);
    setUserProfile({ ...profile, email: email || profile.email });
    if (isDemoEmail(email)) {
      setRole(ROLES.USER);
      setPermissions(getDemoBrowsePermissions());
      return;
    }
    setRole(nextRole);
    setPermissions(nextRole === ROLES.AUDITOR ? getPermissionsForRole(nextRole) : profile.permissions || getPermissionsForRole(nextRole));
  }, []);

  const refreshProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      applyProfile(null);
      return null;
    }
    if (isDemoEmail(firebaseUser.email)) {
      setDemoMode(true);
      const profile = {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || "Demo User",
        role: ROLES.USER,
        permissions: getDemoBrowsePermissions()
      };
      applyProfile(profile, firebaseUser.email);
      return profile;
    }
    const existing = await UserService.getUserProfile(firebaseUser.uid).catch(() => null);
    if (existing?.disabled === true) {
      setSignOutReason("disabled");
      await signOut(auth);
      applyProfile(null);
      throw disabledError();
    }
    try {
      if (isFullAdminEmail(firebaseUser.email)) await RoleService.ensureRolesSeeded();
      const profile = await UserService.ensureUserProfile(firebaseUser);
      applyProfile(profile, firebaseUser.email);
      return profile;
    } catch (err) {
      console.error("Failed to load user profile/roles:", err);
      const fallbackRole = isFullAdminEmail(firebaseUser.email) ? ROLES.FULL_ADMIN : ROLES.USER;
      applyProfile({
        id: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        role: fallbackRole,
        permissions: getPermissionsForRole(fallbackRole)
      }, firebaseUser.email);
      return null;
    }
  }, [applyProfile]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const demo = isDemoEmail(firebaseUser.email);
        setDemoMode(demo);
        setUser(firebaseUser);
        const wsId = localStorage.getItem("futuredoc_workspace");
        if (wsId) setWorkspaceId(wsId);
        try {
          await refreshProfile(firebaseUser);
        } catch (err) {
          if (err?.code !== ACCOUNT_DISABLED) throw err;
        }
      } else {
        setDemoMode(false);
        setUser(null);
        applyProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [refreshProfile, applyProfile]);

  // An administrator can deactivate the account while it is signed in.
  useEffect(() => {
    if (!user?.uid || isDemoEmail(user.email)) return undefined;
    return onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists() && snap.data().disabled === true) {
        setSignOutReason("disabled");
        signOut(auth).catch(() => {});
      }
    }, () => {});
  }, [user?.uid, user?.email]);

  useEffect(() => {
    const auditor = !!user?.uid && normalizeRole(userProfile?.role) === ROLES.AUDITOR;
    setReadOwner(auditor ? userProfile?.parentUid : null);
  }, [user?.uid, userProfile?.role, userProfile?.parentUid]);

  // Records created from now on carry the license and the active workspace
  // (4.2); lists switch to the workspace once membership is confirmed and
  // `config/app.workspaceScope` is on (4.3).
  useEffect(() => {
    const uid = user?.uid;
    if (!uid || !userProfile || isDemoEmail(user.email)) {
      setTenancy(null);
      setWorkspaceScope(false);
      setUserScope(null);
      setGroupCapacity(null);
      setOrganization({ tenantId: null, legalEntities: [], scope: null, legalEntityId: null });
      return undefined;
    }
    let cancelled = false;
    const licenseCode = licenseCodeOf(userProfile);
    const fallback = defaultWorkspaceId(tenantOf(userProfile, uid));
    setTenancy({ licenseCode, workspaceId: workspaceId || fallback, tenantId: tenantOf(userProfile, uid) });
    (async () => {
      try {
        const defaultId = await WorkspaceService.ensureDefaultWorkspace(userProfile, uid);
        const mine = await WorkspaceService.getWorkspaces(uid);
        let active = mine.some((w) => w.id === workspaceId) ? workspaceId : defaultId;
        // Legal entities and the user's scope (4.1, 4.3); a workspace outside
        // the scope falls back to the first allowed legal entity.
        const tenantId = tenantOf(userProfile, uid);
        const tenantProfile = tenantId === uid ? userProfile : await UserService.getUserProfile(tenantId).catch(() => null);
        const codes = [tenantProfile?.licenseCode, ...(tenantProfile?.licenseCodes || [])].filter(Boolean);
        const [legalEntities, rawScope, licenses] = await Promise.all([
          LegalEntityService.list(tenantId).catch(() => []),
          UserScopeService.get(uid).catch(() => null),
          codes.length ? LicenseService.getLicensesByCodes(codes).catch(() => []) : []
        ]);
        setGroupCapacity(groupCapacity(licenses));
        const scope = normalizeScope(rawScope);
        const allowed = scope ? legalEntities.filter((le) => scope.legalEntityIds.includes(le.id)) : legalEntities;
        let legalEntityId = activeLegalEntity({ workspace: mine.find((w) => w.id === active), legalEntities: allowed });
        if (scope && legalEntityId && !mine.some((w) => w.id === active && (w.legalEntityId || null) === legalEntityId)) {
          const target = allowed.find((le) => le.id === legalEntityId);
          if (target?.defaultWorkspaceId) {
            await WorkspaceService.join(target.defaultWorkspaceId, uid).catch(() => null);
            active = target.defaultWorkspaceId;
          }
        }
        const config = await getDoc(doc(db, "config", "app")).catch(() => null);
        if (cancelled) return;
        setUserScope(rawScope);
        setOrganization({ tenantId, legalEntities, scope, legalEntityId });
        setTenancy({ licenseCode, workspaceId: active, legalEntityId, tenantId });
        setWorkspaceScope(!!active && config?.exists() && config.data().workspaceScope === true);
        if (active !== workspaceId) setWorkspaceId(active);
      } catch (err) {
        console.warn("[workspace] default workspace not ready:", err?.code || err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid, user?.email, userProfile, workspaceId, orgVersion]);

  const isDemoUser = useCallback(() => {
    return isDemoEmail(user?.email);
  }, [user]);

  const hasPermission = useCallback((key) => {
    if (isDemoUser()) {
      if (isAdminPanelPermission(key)) return false;
      return checkPermission(getDemoBrowsePermissions(), key);
    }
    return checkPermission(permissions, key);
  }, [permissions, isDemoUser]);

  const canWrite = useCallback(() => {
    if (isDemoUser()) return false;
    return canWriteWithPermissions(permissions);
  }, [permissions, isDemoUser]);

  const isFullAdmin = useCallback(() => {
    if (isDemoEmail(user?.email)) return false;
    return role === ROLES.FULL_ADMIN || isFullAdminEmail(user?.email);
  }, [role, user]);

  const login = useCallback(async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await refreshProfile(cred.user);
    void LoginLogService.record({
      uid: cred.user.uid,
      email: cred.user.email,
      displayName: cred.user.displayName,
      source: "portal_web"
    });
    return cred.user;
  }, [refreshProfile]);

  const register = useCallback(async (email, password, displayName, licenseCode) => {
    const isPlatformOwner = isFullAdminEmail(email);
    let cleanCode = null;

    if (!isPlatformOwner) {
      cleanCode = licenseCode.replace(/-/g, "").toUpperCase();
      const licenseRef = doc(db, "licenses", cleanCode);

      let licenseData = null;
      await runTransaction(db, async (transaction) => {
        const licenseDoc = await transaction.get(licenseRef);
        if (!licenseDoc.exists()) {
          throw new Error("LICENSE_NOT_FOUND");
        }
        const data = licenseDoc.data();
        if (data.activated) {
          throw new Error("LICENSE_ALREADY_ACTIVATED");
        }
        licenseData = data;
      });
      if (await isForeignLicenseBlockedInTurkey(licenseData)) {
        throw new Error("LICENSE_NOT_FOR_TURKEY");
      }
    }

    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });

    if (!isPlatformOwner && cleanCode) {
      const licenseRef = doc(db, "licenses", cleanCode);
      await setDoc(licenseRef, { activated: true, activatedBy: cred.user.uid, activatedAt: new Date() }, { merge: true });
    }

    const initialRole = isPlatformOwner ? ROLES.FULL_ADMIN : ROLES.USER;
    await UserService.ensureUserProfile(cred.user, {
      displayName,
      licenseCode: cleanCode,
      role: initialRole
    });
    if (isPlatformOwner) await RoleService.ensureRolesSeeded();
    await refreshProfile(cred.user);

    return cred.user;
  }, [refreshProfile]);

  const logout = useCallback(async () => {
    await endCurrentSession();
    clearReauth();
    setTenancy(null);
    setWorkspaceScope(false);
    setUserScope(null);
    await signOut(auth);
    applyProfile(null);
  }, [applyProfile]);

  const resetPassword = useCallback(async (email) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
    await updatePassword(auth.currentUser, newPassword);
  }, [user]);

  const updateUserProfile = useCallback(async (data) => {
    if (isDemoEmail(user?.email) || isDemoEmail(auth.currentUser?.email)) {
      if (data.displayName) {
        await updateProfile(auth.currentUser, { displayName: data.displayName });
      }
      setUser({ ...auth.currentUser });
      applyProfile({
        id: auth.currentUser.uid,
        email: auth.currentUser.email,
        displayName: data.displayName || auth.currentUser.displayName || "Demo User",
        role: ROLES.USER,
        permissions: getDemoBrowsePermissions()
      }, auth.currentUser.email);
      return;
    }
    if (data.displayName) {
      await updateProfile(auth.currentUser, { displayName: data.displayName });
    }
    await setDoc(doc(db, "users", auth.currentUser.uid), data, { merge: true });
    setUser({ ...auth.currentUser });
    await refreshProfile(auth.currentUser);
  }, [refreshProfile, applyProfile, user]);

  const deleteAccount = useCallback(async () => {
    if (isDemoEmail(user?.email) || isDemoEmail(auth.currentUser?.email)) {
      throw new Error("DEMO_ACCOUNT_LOCKED");
    }
    await deleteUser(auth.currentUser);
  }, [user]);

  const switchWorkspace = useCallback((wsId) => {
    setWorkspaceId(wsId);
    localStorage.setItem("futuredoc_workspace", wsId);
  }, []);

  /** Books new records to `legalEntityId` by moving to its default workspace. */
  const switchLegalEntity = useCallback(async (legalEntityId) => {
    const le = organization.legalEntities.find((x) => x.id === legalEntityId);
    if (!le?.defaultWorkspaceId || !user?.uid) return;
    await WorkspaceService.join(le.defaultWorkspaceId, user.uid);
    switchWorkspace(le.defaultWorkspaceId);
  }, [organization.legalEntities, user, switchWorkspace]);

  const reloadOrganization = useCallback(() => setOrgVersion((v) => v + 1), []);

  const value = {
    user,
    userProfile,
    role,
    permissions,
    loading,
    isDemoUser,
    hasPermission,
    canWrite,
    isFullAdmin,
    assignableRoles: getAssignableRoles(role),
    canManageUserRole: (targetRole) => canManageUser(role, targetRole),
    refreshProfile: () => refreshProfile(auth.currentUser),
    workspaceId,
    login,
    register,
    logout,
    resetPassword,
    changePassword,
    updateUserProfile,
    deleteAccount,
    switchWorkspace,
    organization,
    switchLegalEntity,
    reloadOrganization,
    ROLES
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
